import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider,
  CircularProgress, Chip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CfpThreeDotsMenu from '../comps/CfpThreeDotsMenu'
import SyncIcon from '@mui/icons-material/Sync'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import InputModal from './modals/InputModal'
import ChatPanel from './chat/ChatPanel'
import KbFolderPanel from './knowledgebase/KbFolderPanel'
import { KbFileRows, KbFilesSection } from './knowledgebase/KbFileTable'
import SearchingScope from '../comps/SearchingScope'
import ConfirmModal from './modals/ConfirmModal'
import KbAddFilesModal from './modals/KbAddFilesModal'
import DocPreviewModal from './modals/DocPreviewModal'
import { FS_XS, ICON_MD, ICON_XL } from '../common/theme'
import {
  type KbItem, type KbFileView,
  treeNodeAt,
  fetchDirectory, fetchInit, addFolder, deleteFolder, renameFolder,
  deleteDocBatch, downloadDoc,
} from '../services/KnowledgeBaseService'
import { addPendingTask } from '../services/TaskManagerService'
import { useApp, useAppActions, useNav } from '../AppContext'
import { addToast } from '../services/Toasts'

// ─── URL helpers ──────────────────────────────────────────────────────────────
// /app/knowledge[/bid][/<seg>/<seg>...] mirrors the current folder tab and
// path. The `bid` segment is inserted between when the user is in the project
// folder tab so bid roots are visually distinct from knowledge folders.

type KbFolderTab = 'knowledge' | 'project'

function buildKbUrl(folderTab: KbFolderTab, currentpath: string): string {
  const cleaned = currentpath.replace(/^\/+|\/+$/g, '')
  const segs = cleaned ? cleaned.split('/').map(encodeURIComponent) : []
  const prefix = folderTab === 'project' ? '/app/knowledge/bid' : '/app/knowledge'
  return segs.length ? `${prefix}/${segs.join('/')}` : prefix
}

function parseKbUrl(): { folderTab: KbFolderTab; path: string } {
  const parts = window.location.pathname.split('/').filter(Boolean)
  if (parts[0] !== 'app' || parts[1] !== 'knowledge') return { folderTab: 'knowledge', path: '/' }
  let folderTab: KbFolderTab = 'knowledge'
  let rest = parts.slice(2)
  if (rest[0] === 'bid') {
    folderTab = 'project'
    rest = rest.slice(1)
  }
  const path = rest.length ? '/' + rest.map(decodeURIComponent).join('/') : '/'
  return { folderTab, path }
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function KnowledgeBaseTab() {
  const initialKbUrl = parseKbUrl()
  const [kbFileView, setKbFileView] = useState<KbFileView>({
    currentpath: initialKbUrl.path,
    foldercount: 0,
    fileSys: [],
    currentfiles: [],
    currentdir: null,
    refreshing: false,
  })
  const { pendingKbPath } = useApp()
  const { setPendingKbPath, updateQuestion } = useAppActions()
  const { activeTab } = useNav()
  const [inReindex, setInReindex] = useState(false)
  const [addFolderOpen, setAddFolderOpen] = useState(false)
  const [deleteFolderOpen, setDeleteFolderOpen] = useState(false)
  const [renameFolderOpen, setRenameFolderOpen] = useState(false)
  const [addFilesOpen, setAddFilesOpen] = useState(false)
  const [addFilesInitial, setAddFilesInitial] = useState<File[]>([])
  const [deleteFilesOpen, setDeleteFilesOpen] = useState(false)
  const [deletingDocShas, setDeletingDocShas] = useState<Set<string>>(new Set())
  const [isInlineDragging, setIsInlineDragging] = useState(false)
  const [folderTab, setFolderTab] = useState<'knowledge' | 'project'>(initialKbUrl.folderTab)
  // const [pendingTasks, setPendingTasks] = useState<string[]>([])
  const currentPathRef = useRef('/')
  const currentDirRef = useRef<KbItem | null>(null)

  const [docPreviewOpen, setDocPreviewOpen] = useState(false)
  const [docPreviewDocsha, setDocPreviewDocsha] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const [folderPanelWidth, setFolderPanelWidth] = useState(220)
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(220)
  const [sortCol, setSortCol] = useState<string>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [kbFilesHeight, setKbFilesHeight] = useState(280)
  const [kbChatActive, setKbChatActive] = useState(false)
  const rhsPanelRef = useRef<HTMLDivElement>(null)
  const isChatResizingRef = useRef(false)
  const chatResizeStartYRef = useRef(0)
  const chatResizeStartHeightRef = useRef(280)

  const isProjectDir = !!(kbFileView.currentdir?.projectsha)
  const projectFiles = kbFileView.currentfiles.filter(f => !f.buyer)
  const buyerFiles = kbFileView.currentfiles.filter(f => f.buyer)
  const anySelected = kbFileView.currentfiles.some(f => f.isselected)
  const selectedCount = kbFileView.currentfiles.filter(f => f.isselected).length
  const allSelected = kbFileView.currentfiles.length > 0 && selectedCount === kbFileView.currentfiles.length

  // ── Data loading ───────────────────────────────────────────────────────────

  const kbDirectory = useCallback(async (dir: KbItem) => {
    setKbFileView(prev => ({ ...prev, refreshing: true, currentfiles: [] }))
    try {
      const items = await fetchDirectory(dir.dirsha ?? null, dir.projectsha ?? null)
      setKbFileView(prev => ({ ...prev, currentfiles: items, refreshing: false }))
    } catch {
      setKbFileView(prev => ({ ...prev, refreshing: false }))
    }
  }, [])

  const refreshFilesSilent = useCallback(async () => {
    const dir = currentDirRef.current
    if (!dir) return
    try {
      const items = await fetchDirectory(dir.dirsha ?? null, dir.projectsha ?? null)
      setKbFileView(prev => ({ ...prev, currentfiles: items }))
    } catch { }
  }, [])

  const kbInit = useCallback(async (path?: string) => {
    const targetPath = path ?? kbFileView.currentpath
    setKbFileView(prev => ({ ...prev, refreshing: true }))
    try {
      const tree = await fetchInit(targetPath)
      if (tree) {
        const node = treeNodeAt(tree, targetPath)
        const children: KbItem[] = node.child ?? []
        setKbFileView({
          currentpath: targetPath,
          foldercount: children.filter(c => c.isDirectory).length,
          fileSys: children.filter(c => c.isDirectory),
          currentfiles: [],
          currentdir: node,
          refreshing: true,
        })
        await kbDirectory(node)
      }
    } catch {
      setKbFileView(prev => ({ ...prev, refreshing: false }))
    }
  }, [kbFileView.currentpath, kbDirectory])

  // Load whatever path is in the URL on first mount.
  useEffect(() => { kbInit(initialKbUrl.path) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror folder-tab + currentpath into the URL while the KB tab is visible.
  useEffect(() => {
    if (activeTab !== 'knowledge') return
    const target = buildKbUrl(folderTab, kbFileView.currentpath)
    if (window.location.pathname !== target) {
      window.history.pushState({}, '', target)
    }
  }, [folderTab, kbFileView.currentpath, activeTab])

  // Re-derive folder-tab + path from the URL on back/forward.
  useEffect(() => {
    const onPop = () => {
      const { folderTab: ft, path } = parseKbUrl()
      setFolderTab(ft)
      if (path !== currentPathRef.current) kbInit(path)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [kbInit])

  useEffect(() => {
    if (!pendingKbPath) return
    kbInit(pendingKbPath)
    setPendingKbPath(null)
  }, [pendingKbPath]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { currentPathRef.current = kbFileView.currentpath }, [kbFileView.currentpath])
  useEffect(() => { currentDirRef.current = kbFileView.currentdir }, [kbFileView.currentdir])


  // ── Resize ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (isResizingRef.current) {
        const delta = e.clientX - startXRef.current
        const maxW = mainContainerRef.current?.clientWidth ?? window.innerWidth
        setFolderPanelWidth(Math.max(120, Math.min(maxW - 160, startWidthRef.current + delta)))
      } else if (isChatResizingRef.current) {
        const delta = e.clientY - chatResizeStartYRef.current
        const maxH = (rhsPanelRef.current?.clientHeight ?? window.innerHeight) - 160
        setKbFilesHeight(Math.max(80, Math.min(maxH, chatResizeStartHeightRef.current + delta)))
      }
    }
    const onUp = () => {
      if (isResizingRef.current || isChatResizingRef.current) {
        isResizingRef.current = false
        isChatResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
  }, [])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startXRef.current = e.clientX
    startWidthRef.current = folderPanelWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const handleChatResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isChatResizingRef.current = true
    chatResizeStartYRef.current = e.clientY
    chatResizeStartHeightRef.current = kbFilesHeight
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  const openFolder = (folder: KbItem) => {
    const base = kbFileView.currentpath.endsWith('/') ? kbFileView.currentpath : kbFileView.currentpath + '/'
    const newPath = base + folder.name
    setKbFileView(prev => ({
      ...prev,
      currentpath: newPath,
      fileSys: folder.child?.filter(c => c.isDirectory) ?? [],
      foldercount: folder.child?.filter(c => c.isDirectory).length ?? 0,
      currentdir: folder,
      currentfiles: [],
    }))
    kbDirectory(folder)
  }

  const goHome = () => kbInit('/')

  const goUp = () => {
    const parts = kbFileView.currentpath.replace(/\/$/, '').split('/')
    parts.pop()
    kbInit(parts.join('/') || '/')
  }

  // ── Folder actions ─────────────────────────────────────────────────────────

  const handleAddFolder = async (name: string) => {
    try {
      const existing = new Set(kbFileView.fileSys.filter(f => f.isDirectory).map(f => f.name.toLowerCase()))
      if (existing.has(name.toLowerCase())) {
        addToast('error', 'A folder with that name already exists');
        return;
      }
      const result = await addFolder(name, kbFileView.currentdir?.dirsha)
      if (result.success) {
        kbInit(kbFileView.currentpath)
      } else {
        addToast('error', result.msg || 'Failed to add folder')
      }
    } catch {
      addToast('error', 'Failed to add folder')
    }
  }

  const handleDeleteFolder = async () => {
    try {
      const ret = await deleteFolder(kbFileView.currentdir?.dirsha ?? '',
        kbFileView.currentpath)
      const newpath = ret?.newpath ?? '/';
      kbInit(newpath);
      //toast
      (ret?.success ?? false) ? addToast('success', 'Directory Deleted') : addToast('error', ret?.msg ?? 'error');
    } catch { }
  }

  const handleRenameFolder = async (name: string) => {
    try {
      await renameFolder(name, kbFileView.currentdir?.dirsha ?? '', kbFileView.currentpath)
      kbInit(kbFileView.currentpath)
    } catch { }
  }

  // ── File actions ───────────────────────────────────────────────────────────
  const clearFileSelection = () => {
    setKbFileView(prev => ({
      ...prev,
      currentfiles: prev.currentfiles.map(f => f.isselected ? { ...f, isselected: false } : f),
    }))
  }

  const handleDeleteFiles = async () => {
    const toDelete = kbFileView.currentfiles.filter(f => f.isselected && f.docsha)
    const docshas = toDelete.map(f => f.docsha as string)
    if (docshas.length === 0) return
    setDeletingDocShas(prev => new Set([...prev, ...docshas]))
    clearFileSelection()
    try {
      const taskshalist = await deleteDocBatch(docshas)
      taskshalist.forEach((sha, i) => {
        const docsha = docshas[i];
        addPendingTask(sha, addToast, updateQuestion, () => {
          setKbFileView(prev => ({ ...prev, currentfiles: prev.currentfiles.filter(f => f.docsha !== docsha) }))
          setDeletingDocShas(prev => { const next = new Set(prev); next.delete(docsha); return next; })
        })
      })
    } catch {
      // leave deletingDocShas in place - pending task refresh will reconcile
    }
  }

  const handleDownloadFiles = async () => {
    const toDownload = kbFileView.currentfiles.filter(f => f.isselected && f.docsha)
    if (toDownload.length === 0) return
    for (const f of toDownload) {
      await downloadDoc(f.docsha as string, f.name)
    }
    clearFileSelection()
  }

  const handleFilesUploaded = (uploadedFiles: File[], taskshalist: string[]) => {
    const placeholders: KbItem[] = uploadedFiles.map(f => ({
      name: f.name,
      isDirectory: false,
      isIndexed: false,
      savedate: new Date().toISOString(),
    }))
    setKbFileView(prev => ({
      ...prev,
      currentfiles: [
        ...prev.currentfiles,
        ...placeholders.filter(p => !prev.currentfiles.some(e => e.name === p.name)),
      ],
    }))
    taskshalist.forEach(sha => {
      // setPendingTasks(prev => [...prev, sha])
      addPendingTask(sha, addToast, updateQuestion, () => {
        // setPendingTasks(prev => prev.filter(s => s !== sha))
        refreshFilesSilent()
      })
    })
  }

  const openAddFilesWithDropped = (fileList: FileList | null) => {
    setAddFilesInitial(fileList ? Array.from(fileList) : [])
    setAddFilesOpen(true)
  }

  // ── Doc preview ────────────────────────────────────────────────────────────

  const openDocPreview = useCallback((docsha: string) => {
    setDocPreviewDocsha(docsha)
    setDocPreviewOpen(true)
  }, [])

  // ── File selection ─────────────────────────────────────────────────────────

  const toggleSelect = (file: KbItem, e: React.MouseEvent) => {
    e.stopPropagation()
    setKbFileView(prev => ({
      ...prev,
      currentfiles: prev.currentfiles.map(f => f === file ? { ...f, isselected: !f.isselected } : f),
    }))
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  // selectMultipleBool is referenced in renderFileRows but the toggle button is commented out;
  // keep the state to avoid breaking the opacity logic if re-enabled later
  const selectMultipleBool = false

  return (
    <Box ref={mainContainerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      <KbAddFilesModal
        open={addFilesOpen}
        dirsha={kbFileView.currentdir?.dirsha}
        initialFiles={addFilesInitial}
        existingNames={kbFileView.currentfiles.map(f => f.name)}
        onClose={() => setAddFilesOpen(false)}
        onUploaded={handleFilesUploaded}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.doc,.xls,.xlsx,.csv,.txt,.rtf"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files?.length) { openAddFilesWithDropped(e.target.files); e.target.value = '' } }}
      />

      <ConfirmModal
        open={deleteFilesOpen}
        title="Delete Files"
        body={`Delete ${selectedCount} selected file${selectedCount !== 1 ? 's' : ''}? This cannot be undone.`}
        okLabel="Delete"
        testIdPrefix="kb_deletefiles"
        onConfirm={handleDeleteFiles}
        onClose={() => setDeleteFilesOpen(false)}
      />

      <InputModal
        open={addFolderOpen}
        title="Add Folder"
        label="Folder name"
        okLabel="Add"
        singleLine
        testIdPrefix="kb_addfolder"

        onConfirm={handleAddFolder}
        onClose={() => setAddFolderOpen(false)}
      />
      <ConfirmModal
        open={deleteFolderOpen}
        title="Delete Folder"
        body={`Delete "${kbFileView.currentdir?.name}"? This cannot be undone.`}
        okLabel="Delete"
        testIdPrefix="kb_deletefolder"
        onConfirm={handleDeleteFolder}
        onClose={() => setDeleteFolderOpen(false)}
      />
      <InputModal
        open={renameFolderOpen}
        title="Rename Folder"
        label="New name"
        okLabel="Rename"
        singleLine
        testIdPrefix="kb_renamefolder"
        initialValue={kbFileView.currentdir?.name ?? ''}
        onConfirm={handleRenameFolder}
        onClose={() => setRenameFolderOpen(false)}
      />

      {/* ── Top toolbar ──────────────────────────────────────────────── */}
      <Box data-testid="kb_toolbar" sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.25 }}>
        <Typography variant="caption" sx={{ opacity: 0.6, mr: 0.25 }}>Folders</Typography>
        <Tooltip title="Refresh Knowledge Base">
          <IconButton size="small" data-testid="kb_refresh_btn" onClick={() => kbInit()}><RefreshIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
        <Tooltip title="Add Folder">
          <IconButton size="small" data-testid="kb_addfolder_btn" onClick={() => setAddFolderOpen(true)}><CreateNewFolderOutlinedIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
        <Tooltip title="Delete Folder">
          <span>
            <IconButton size="small" data-testid="kb_deletefolder_btn"
              disabled={kbFileView.currentpath === '/'} onClick={() => setDeleteFolderOpen(true)}><DeleteOutlineIcon sx={{ fontSize: ICON_MD }} /></IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rename Folder">
          <span>
            <IconButton size="small" data-testid="kb_renamefolder_btn"
              disabled={kbFileView.currentpath === '/'} onClick={() => setRenameFolderOpen(true)}><EditIcon sx={{ fontSize: ICON_MD }} /></IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Typography variant="caption" sx={{ opacity: 0.6, mr: 0.25 }}>Files</Typography>
        <Tooltip title="Add Files">
          <IconButton size="small" data-testid="kb_addfiles_btn" onClick={() => setAddFilesOpen(true)}><NoteAddOutlinedIcon sx={{ fontSize: ICON_MD }} /></IconButton>
        </Tooltip>
        <Tooltip title="Delete Files">
          <span>
            <IconButton size="small" data-testid="kb_deletefiles_btn"
              disabled={!anySelected} onClick={() => setDeleteFilesOpen(true)}><DeleteOutlineIcon sx={{ fontSize: ICON_MD }} /></IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download">
          <span>
            <IconButton size="small" data-testid="kb_downloadfiles_btn"
              disabled={!anySelected} onClick={handleDownloadFiles}><FileDownloadOutlinedIcon sx={{ fontSize: ICON_MD }} /></IconButton>
          </span>
        </Tooltip>
        <Tooltip title={allSelected ? 'Deselect All' : 'Select All'}>
          <IconButton size="small"
            data-testid="kb_selectall_btn"
            onClick={() => setKbFileView(prev => ({ ...prev, currentfiles: prev.currentfiles.map(f => ({ ...f, isselected: !allSelected })) }))}
            sx={{ borderRadius: 1, bgcolor: allSelected ? 'primary.main' : 'transparent', color: allSelected ? '#fff' : 'inherit', '&:hover': { bgcolor: allSelected ? 'primary.dark' : 'action.hover' } }}>
            <SelectAllIcon sx={{ fontSize: ICON_MD }} />
          </IconButton>
        </Tooltip>
        {selectedCount > 0 && (
          <Chip
            size="small"
            label={`${selectedCount} selected`}
            color="primary"
            onDelete={() => setKbFileView(prev => ({ ...prev, currentfiles: prev.currentfiles.map(f => ({ ...f, isselected: false })) }))}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )}
        {/* {pendingTasks.length > 0 && (
          <Chip
            size="small"
            icon={<CircularProgress size={10} sx={{ ml: '6px !important' }} />}
            label={`Processing ${pendingTasks.length} file${pendingTasks.length !== 1 ? 's' : ''}`}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )} */}

        <Box sx={{ flex: 1 }} />

        <CfpThreeDotsMenu
          testId="kb_moreoptions_btn"
          items={[{
            label: 'Reindex All Documents',
            icon: <SyncIcon sx={{ fontSize: ICON_MD }} />,
            disabled: inReindex,
            testId: 'kb_reindex_menuitem',
            onClick: () => { if (!inReindex) setInReindex(true) },
          }]}
        />
      </Box>

      {/* ── Path bar ─────────────────────────────────────────────────── */}
      <Box data-testid="kb_pathbar" sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 0.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.25 }}>
        <Tooltip title="Go Home">
          <span>
            <IconButton size="small" data-testid="kb_gohome_btn"
              disabled={kbFileView.currentpath === '/'} onClick={goHome}>
              <HomeOutlinedIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Up Folder">
          <span>
            <IconButton size="small" data-testid="kb_goup_btn"
              disabled={kbFileView.currentpath === '/'} onClick={goUp}>
              <ArrowUpwardIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" data-testid="kb_currentpath"
          sx={{ flex: 1, px: 0.75, py: 0.25, mx: 0.5, bgcolor: 'action.hover', borderRadius: 0.5, fontSize: FS_XS }}>
          {kbFileView.currentpath}
        </Typography>
      </Box>

      {/* ── Split panel ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* ── Folder panel (LHS) ──────────────────────────────────── */}
        <Box sx={{ flex: `0 0 ${folderPanelWidth}px`, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <KbFolderPanel
            currentPath={kbFileView.currentpath}
            currentDirName={kbFileView.currentdir?.name}
            folders={kbFileView.fileSys}
            refreshing={kbFileView.refreshing}
            folderTab={folderTab}
            onFolderTabChange={setFolderTab}
            onOpenFolder={openFolder}
          />
        </Box>

        {/* ── Resizable divider ────────────────────────────────────── */}
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: 7, flexShrink: 0,
            borderLeft: 1, borderRight: 1, borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'col-resize !important',
            '& *': { cursor: 'col-resize !important' },
            '&:hover': { bgcolor: 'action.hover' },
            zIndex: 1,
          }}
        >
          <MoreVertIcon sx={{ fontSize: ICON_XL, opacity: 0.35 }} />
        </Box>

        {/* ── Files panel (RHS) ────────────────────────────────────── */}
        <Box ref={rhsPanelRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* Files section - resizable top */}
          <Box sx={{ flex: kbChatActive ? `0 0 ${kbFilesHeight}px` : 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            {(() => {
              const rowsCommon = {
                sortCol, sortDir,
                onSortChange: (c: string, d: 'asc' | 'desc') => { setSortCol(c); setSortDir(d) },
                deletingDocShas,
                selectMultipleBool,
                isInlineDragging,
                onSetInlineDragging: setIsInlineDragging,
                onDropFiles: openAddFilesWithDropped,
                onClickEmpty: () => fileInputRef.current?.click(),
                onToggleSelect: toggleSelect,
                onOpen: openDocPreview,
              }
              if (kbFileView.refreshing) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}><CircularProgress size={22} /></Box>
              if (!isProjectDir) return <KbFileRows files={kbFileView.currentfiles} {...rowsCommon} />
              return (
                <>
                  <KbFilesSection label="Project Sources" files={projectFiles} {...rowsCommon} />
                  <KbFilesSection label="Buyer Sources" files={buyerFiles} {...rowsCommon} />
                </>
              )
            })()}
          </Box>

          {/* Chat resize handle - only shown once a conversation is active */}
          {kbChatActive && (
            <Box
              onMouseDown={handleChatResizeStart}
              sx={{
                height: 7, flexShrink: 0,
                cursor: 'row-resize !important',
                borderTop: 1, borderBottom: 1, borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                '& *': { cursor: 'row-resize !important' },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <MoreHorizIcon sx={{ fontSize: ICON_XL, opacity: 0.35 }} />
            </Box>
          )}

          {/* Chat panel - owns its own flex sizing now: collapses to the
              composer's natural height when no conversation, grows to fill
              the remaining pane height once messages start. */}
          <ChatPanel
            currentPath={kbFileView.currentpath}
            currentFolderName={kbFileView.currentdir?.name}
            toolbarScopeContent={
              <SearchingScope
                items={kbFileView.currentfiles.filter(f => f.isselected && f.docsha).map(f => ({
                  sha: f.docsha as string,
                  name: f.name,
                  type: 'file' as const,
                }))}
                onRemoveItem={(sha) => setKbFileView(prev => ({
                  ...prev,
                  currentfiles: prev.currentfiles.map(f => f.docsha === sha ? { ...f, isselected: false } : f),
                }))}
                onClear={() => setKbFileView(prev => ({
                  ...prev,
                  currentfiles: prev.currentfiles.map(f => ({ ...f, isselected: false })),
                }))}
                currentPath={kbFileView.currentpath}
                currentDirName={kbFileView.currentdir?.name}
                currentFolderIsProject={!!kbFileView.currentdir?.projectsha}
              />
            }
            onConversationChange={(hasMessages) => {
              if (hasMessages && !kbChatActive) {
                const panelH = rhsPanelRef.current?.clientHeight ?? window.innerHeight
                setKbFilesHeight(Math.round(panelH / 2))
              }
              setKbChatActive(hasMessages)
            }}
          />

        </Box>
      </Box>

      <DocPreviewModal
        open={docPreviewOpen}
        docsha={docPreviewDocsha}
        onClose={() => setDocPreviewOpen(false)}
      />
    </Box>
  )
}
